import { useState } from 'react';
import {
  Box,
  List,
  ListItem,
  Paper,
  Switch,
  Typography,
  IconButton,
  Collapse,
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Tooltip,
} from '@mui/material';
import {
  KeyboardArrowUp as ArrowUpIcon,
  KeyboardArrowDown as ArrowDownIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';

import { BLOCK_REGISTRY } from './blockRegistry';

/**
 * BlockList — lista de blocos do editor de página.
 *
 * Exibe todos os blocos configurados com controles de:
 * - Ativar/desativar (Switch enabled)
 * - Reordenar (setas ↑/↓)
 * - Editar (expande editor inline)
 * - Excluir
 *
 * Botão "+ Adicionar bloco" abre menu filtrado pelos blocos disponíveis
 * para o siteType atual (baseado em allowedIn do BLOCK_REGISTRY).
 *
 * @param {object[]} blocks - array de blocos ordenados conforme §3.3
 * @param {number} siteType - PageType enum int (1=LinkInBio, 2=Professional, 3=Company)
 * @param {function} onUpdate - (blockId, config) => void — atualiza config de um bloco
 * @param {function} onToggle - (blockId) => void — alterna enabled
 * @param {function} onReorder - (fromIndex, toIndex) => void — move bloco; indices em sortedBlocks
 * @param {function} onAdd - (type) => void — adiciona novo bloco do tipo especificado
 * @param {function} onRemove - (blockId) => void — remove bloco
 * @param {object} context - contexto passado aos editores (disponíveis, scope, etc.)
 */
export default function BlockList({
  blocks = [],
  siteType = 'link_in_bio',
  onUpdate,
  onToggle,
  onReorder,
  onAdd,
  onRemove,
  context = {},
}) {
  const [expandedId, setExpandedId] = useState(null);
  const [addMenuAnchor, setAddMenuAnchor] = useState(null);

  const sortedBlocks = [...blocks].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  const availableToAdd = Object.entries(BLOCK_REGISTRY).filter(([type, config]) => {
    const alreadyHas = blocks.some((b) => b.type === type);
    return config.allowedIn.includes(siteType) && !alreadyHas;
  });

  function handleToggleExpanded(blockId) {
    setExpandedId((prev) => (prev === blockId ? null : blockId));
  }

  function handleAddMenuOpen(event) {
    setAddMenuAnchor(event.currentTarget);
  }

  function handleAddMenuClose() {
    setAddMenuAnchor(null);
  }

  function handleAddBlock(type) {
    onAdd?.(type);
    handleAddMenuClose();
  }

  function handleRemove(blockId) {
    if (expandedId === blockId) setExpandedId(null);
    onRemove?.(blockId);
  }

  return (
    <Box>
      {sortedBlocks.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontStyle: 'italic' }}>
          Nenhum bloco adicionado. Clique em &quot;+ Adicionar bloco&quot; para começar.
        </Typography>
      )}

      <List disablePadding>
        {sortedBlocks.map((block, index) => {
          const registryEntry = BLOCK_REGISTRY[block.type];
          if (!registryEntry) return null;

          const { label, icon: BlockIcon, editorComponent: EditorComponent } = registryEntry;
          const isExpanded = expandedId === block.id;
          const isFirst = index === 0;
          const isLast = index === sortedBlocks.length - 1;

          return (
            <Paper
              key={block.id}
              variant="outlined"
              sx={{
                mb: 1,
                opacity: block.enabled ? 1 : 0.6,
                transition: 'opacity 0.2s ease',
              }}
            >
              <ListItem
                disablePadding
                sx={{
                  px: 1.5,
                  py: 0.5,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                }}
              >
                {/* Enabled toggle */}
                <Tooltip title={block.enabled ? 'Desativar bloco' : 'Ativar bloco'}>
                  <Switch
                    size="small"
                    checked={block.enabled ?? true}
                    onChange={() => onToggle?.(block.id)}
                    inputProps={{ 'aria-label': `${block.enabled ? 'Desativar' : 'Ativar'} bloco ${label}` }}
                  />
                </Tooltip>

                {/* Icon + label */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                  <BlockIcon sx={{ fontSize: 18, color: 'text.secondary', flexShrink: 0 }} />
                  <Typography
                    variant="body2"
                    fontWeight={block.enabled ? 600 : 400}
                    noWrap
                    title={label}
                  >
                    {label}
                  </Typography>
                </Box>

                {/* Reorder buttons */}
                <Tooltip title="Mover para cima">
                  <span>
                    <IconButton
                      size="small"
                      disabled={isFirst}
                      onClick={() => onReorder?.(index, index - 1)}
                      aria-label={`Mover ${label} para cima`}
                    >
                      <ArrowUpIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Mover para baixo">
                  <span>
                    <IconButton
                      size="small"
                      disabled={isLast}
                      onClick={() => onReorder?.(index, index + 1)}
                      aria-label={`Mover ${label} para baixo`}
                    >
                      <ArrowDownIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>

                {/* Edit toggle */}
                {EditorComponent && (
                  <Tooltip title={isExpanded ? 'Fechar editor' : 'Editar bloco'}>
                    <IconButton
                      size="small"
                      onClick={() => handleToggleExpanded(block.id)}
                      color={isExpanded ? 'primary' : 'default'}
                      aria-label={`${isExpanded ? 'Fechar' : 'Editar'} bloco ${label}`}
                      aria-expanded={isExpanded}
                    >
                      {isExpanded ? <ExpandLessIcon fontSize="small" /> : <EditIcon fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                )}

                {/* Remove */}
                <Tooltip title="Excluir bloco">
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleRemove(block.id)}
                    aria-label={`Excluir bloco ${label}`}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </ListItem>

              {/* Inline editor (collapsed by default) */}
              {EditorComponent && (
                <Collapse in={isExpanded} unmountOnExit>
                  <Divider />
                  <Box sx={{ p: 2 }}>
                    <EditorComponent
                      value={block.config ?? {}}
                      onChange={(newConfig) => onUpdate?.(block.id, newConfig)}
                      context={{ ...context, siteType }}
                    />
                  </Box>
                </Collapse>
              )}
            </Paper>
          );
        })}
      </List>

      {/* Add block button */}
      <Button
        variant="outlined"
        startIcon={<AddIcon />}
        onClick={handleAddMenuOpen}
        disabled={availableToAdd.length === 0}
        size="small"
        sx={{ mt: 1 }}
        aria-label="Adicionar bloco"
        aria-haspopup="true"
        aria-expanded={Boolean(addMenuAnchor)}
      >
        + Adicionar bloco
      </Button>

      <Menu
        anchorEl={addMenuAnchor}
        open={Boolean(addMenuAnchor)}
        onClose={handleAddMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        {availableToAdd.map(([type, config]) => {
          const BlockIcon = config.icon;
          return (
            <MenuItem key={type} onClick={() => handleAddBlock(type)}>
              <ListItemIcon>
                <BlockIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={config.label}
                secondary={config.description.slice(0, 80) + '…'}
                secondaryTypographyProps={{ noWrap: true, style: { maxWidth: 300 } }}
              />
            </MenuItem>
          );
        })}
        {availableToAdd.length === 0 && (
          <MenuItem disabled>
            <ListItemText primary="Todos os blocos disponíveis já foram adicionados." />
          </MenuItem>
        )}
      </Menu>
    </Box>
  );
}
